import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Auth } from "aws-amplify";
import { loadStripe } from "@stripe/stripe-js";
import "./subscriptionPage.css";
import { SubscriptionContext } from "../../reducers/SubscriptionContext";

// Separate Stripe instances for individual and group payments
console.log('Stripe Keys:', {
    individual: process.env.REACT_APP_STRIPE_PUBLIC_KEY,
    group: process.env.REACT_APP_STRIPE_PUBLIC_KEY_GROUP
});

// Fallback keys in case environment variables don't load
const INDIVIDUAL_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_test_51ROTyMPJPKxdDB7wHDBE0C1DedQ1wfHJu4pY51P5sBiQbxqRn8ibswcJqNf1z2N6Wkn7DR9FV8XAHMVEUiZUqsE300Kp4zJwKH";
const GROUP_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY_GROUP || "pk_test_51S8Z898toUC9Q5erBiyI0iPBWe0gcseF13OTImNt6jAE9tO4RoDnqA4tFuLYk7JGVj2IYwAB4EsoKePCwGC7XmhF00DL3FUjIx";

const stripePromiseIndividual = INDIVIDUAL_KEY ? loadStripe(INDIVIDUAL_KEY) : null;
const stripePromiseGroup = GROUP_KEY ? loadStripe(GROUP_KEY) : null;

const SubscriptionPage = () => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('individual');
    const [teamDetails, setTeamDetails] = useState({ schoolName: '' });
    const [studentList, setStudentList] = useState([]);
    const [studentInput, setStudentInput] = useState('');
    const [studentError, setStudentError] = useState('');
    const [jwt, setJwt] = useState('');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

    const history = useHistory();
    const { refreshSubscription } = useContext(SubscriptionContext);

    // Get JWT token using the same method as EditBatch
    const getToken = async () => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken();
            const token = accessToken.getJwtToken();

            // Log token details for debugging
            if (token) {
                const tokenParts = token.split('.');
                if (tokenParts.length > 1) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    console.log('Token details:', {
                        tokenStartsWith: token.substring(0, 10) + '...',
                        tokenLength: token.length,
                        expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'N/A'
                    });
                }
            }

            setJwt(token);
            return token;
        } catch (error) {
            console.error('Error getting JWT token:', error);
            return null;
        }
    };

    const checkSubscriptionStatus = async () => {
        try {
            const token = await getToken();
            if (!token) return;

            const user = await Auth.currentAuthenticatedUser();
            const username = user.username.includes("Google_") ? user.attributes.email : user.username;

            const response = await fetch("https://api.prod.quizwizard.net/quiz/getSubscription", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": token,
                },
                body: JSON.stringify({ username }),
            });

            const data = await response.json();

            // Check if user has an active subscription
            const isActive = data.status === 'active' || data.premiumuser === '1' || data.premiumuser === 1;
            setIsSubscribed(isActive);

            if (isActive) {
                // Force a global state refresh to update the UI elsewhere (e.g. Free Tier message)
                refreshSubscription();
            }

            // If user is subscribed, store the current plan details
            if (isActive) {
                // Get plan details from response
                let planType = data.plan_type || data.planType || data.plan || '';
                let planDuration = data.duration ? parseInt(data.duration) :
                    data.plan_duration ? parseInt(data.plan_duration) : null;

                // If no duration in response, try to get it from the plan type
                if (!planDuration && planType) {
                    const durationMatch = planType.match(/(\d+)/);
                    if (durationMatch) {
                        planDuration = parseInt(durationMatch[0]);
                    }
                }

                // Determine plan category based on plan type or default to individual
                let planCategory = 'individual';
                const planTypeStr = String(planType).toLowerCase();
                if (data.is_group || planTypeStr.includes('group')) {
                    planCategory = 'group';
                }

                // Find matching plan
                let planKey = null;

                // First try exact match with plan type (case-insensitive)
                if (planType) {
                    const normalizedPlanType = planType.toUpperCase().trim();

                    // Check for exact match first
                    if (planDetails[planCategory][normalizedPlanType]) {
                        planKey = normalizedPlanType;
                    }
                    // Try to find a plan that contains the plan type
                    else {
                        const matchingPlan = Object.keys(planDetails[planCategory]).find(key =>
                            key.toUpperCase().includes(normalizedPlanType) ||
                            normalizedPlanType.includes(key.toUpperCase())
                        );

                        if (matchingPlan) {
                            planKey = matchingPlan;
                        }
                    }
                }

                // If still no match, try to match by duration
                if (!planKey && planDuration) {
                    // Find all plans with matching duration
                    const matchingPlans = Object.entries(planDetails[planCategory])
                        .filter(([_, plan]) => plan.duration === planDuration);

                    if (matchingPlans.length > 0) {
                        // If there's only one match, use it
                        if (matchingPlans.length === 1) {
                            planKey = matchingPlans[0][0];
                        }
                        // If multiple matches, try to find the one that best matches the plan type
                        else if (planType) {
                            const planTypeLower = planType.toLowerCase();
                            const bestMatch = matchingPlans.find(([key, _]) =>
                                key.toLowerCase().includes(planTypeLower) ||
                                planTypeLower.includes(key.toLowerCase())
                            );

                            planKey = bestMatch ? bestMatch[0] : matchingPlans[0][0];
                        }
                    }
                }

                // If still no match, try to match by plan name
                if (!planKey) {
                    // Try different variations of the plan name
                    const planNameVariations = [];

                    // Add variations based on the plan type
                    if (planType) {
                        planNameVariations.push(
                            planType.toLowerCase(),
                            planType.toLowerCase().replace(/\s+/g, ''), // Remove spaces
                            planType.toLowerCase().replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric
                        );
                    }

                    // Add variations based on duration
                    if (planDuration) {
                        const durationStr = planDuration.toString();
                        planNameVariations.push(
                            `${durationStr}month`,
                            `${durationStr} month`,
                            `${durationStr}-month`,
                            `${durationStr}_month`
                        );
                    }

                    // Try to find a matching plan using any of the variations
                    const matchingPlan = Object.entries(planDetails[planCategory]).find(
                        ([key, plan]) => {
                            const planName = plan.name.toLowerCase();
                            const planKeyLower = key.toLowerCase();

                            // Check if any variation matches the plan name or key
                            return planNameVariations.some(variation =>
                                planName.includes(variation) ||
                                planKeyLower.includes(variation) ||
                                variation.includes(planName) ||
                                variation.includes(planKeyLower)
                            );
                        }
                    );

                    if (matchingPlan) {
                        planKey = matchingPlan[0];
                    }
                }

                // If still no match, try to find a plan with matching duration
                if (!planKey && planDuration) {
                    const plansWithMatchingDuration = Object.entries(planDetails[planCategory])
                        .filter(([_, p]) => p.duration === planDuration);

                    if (plansWithMatchingDuration.length > 0) {
                        // If multiple plans have the same duration, try to find the one that matches the plan type
                        let matchedPlan = plansWithMatchingDuration[0];

                        if (planType && plansWithMatchingDuration.length > 1) {
                            const planTypeLower = planType.toLowerCase();
                            const exactMatch = plansWithMatchingDuration.find(([key, _]) =>
                                key.toLowerCase() === planTypeLower
                            );
                            matchedPlan = exactMatch || matchedPlan;
                        }

                        planKey = matchedPlan[0];
                    }
                }

                // As a last resort, default to the first available plan
                if (!planKey) {
                    const availablePlans = Object.keys(planDetails[planCategory]);
                    if (availablePlans.length > 0) {
                        // Try to find a 6-month plan first if it exists
                        const sixMonthPlan = availablePlans.find(key =>
                            planDetails[planCategory][key].duration === 6
                        );

                        planKey = sixMonthPlan || availablePlans[0];
                    }
                }

                if (planKey) {
                    const planDetailsToSet = planDetails[planCategory][planKey];

                    // Double check if we have the right plan
                    if (planDuration && planDetailsToSet?.duration !== planDuration) {
                        // Try to find a plan with matching duration if available
                        const plansWithMatchingDuration = Object.entries(planDetails[planCategory])
                            .filter(([_, p]) => p.duration === planDuration);

                        if (plansWithMatchingDuration.length > 0) {
                            const [matchedKey] = plansWithMatchingDuration[0];
                            planKey = matchedKey;
                        }
                    }

                    setCurrentPlan({
                        type: planCategory,
                        plan: planKey,
                        details: planDetails[planCategory][planKey]
                    });
                } else {
                    setCurrentPlan(null);
                }
            } else {
                setCurrentPlan(null);
            }
        } catch (error) {
            // In case of error, assume not subscribed to allow the user to try again
            setIsSubscribed(false);
        } finally {
            setIsLoadingSubscription(false);
        }
    };

    useEffect(() => {
        getToken().then(() => {
            checkSubscriptionStatus();
        });
    }, []);

    useEffect(() => {
        const getToken = async () => {
            try {
                const session = await Auth.currentSession();
                setJwt(session.getIdToken().getJwtToken());
            } catch (error) {
                console.error("Error getting JWT token: ", error);
            }
        };
        getToken();
    }, []);

    const planDetails = {
        individual: {
            MONTHLY: {
                name: "1 Month Plan",
                baseAmount: 2.99,
                type: 'individual',
                duration: 1,
                features: [
                    "1 student account",
                    "1 month access",
                    "Unlimited practice tests",
                    "Progress tracking"
                ]
            },
            QUARTERLY: {
                name: "3 Month Plan",
                baseAmount: 5.99,
                type: 'individual',
                duration: 3,
                features: [
                    "1 student account",
                    "3 months access",
                    "Unlimited practice tests",
                    "Progress tracking"
                ]
            },
            HALFYEARLY: {
                name: "6 Month Plan",
                baseAmount: 9.99,
                type: 'individual',
                duration: 6,
                features: [
                    "1 student account",
                    "6 months access",
                    "Unlimited practice tests",
                    "Progress tracking"
                ]
            },
        },
        group: {
            MONTHLY: {
                name: "1 Month Plan",
                baseAmount: 2.99,
                type: 'group',
                duration: 1,
                features: [
                    "Multiple student accounts",
                    "1 month access",
                    "Unlimited practice tests",
                    "Progress tracking",
                    "Bulk student management"
                ]
            },
            QUARTERLY: {
                name: "3 Month Plan",
                baseAmount: 5.99,
                type: 'group',
                duration: 3,
                features: [
                    "Multiple student accounts",
                    "3 months access",
                    "Unlimited practice tests",
                    "Progress tracking",
                    "Bulk student management"
                ]
            },
            HALFYEARLY: {
                name: "6 Month Plan",
                baseAmount: 9.99,
                type: 'group',
                duration: 6,
                features: [
                    "Multiple student accounts",
                    "6 months access",
                    "Unlimited practice tests",
                    "Progress tracking",
                    "Bulk student management"
                ]
            },
        }
    };

    const handleAddStudent = async () => {
        setStudentError('');
        const username = studentInput.trim();

        // Input validation
        if (!username) {
            setStudentError("Please enter a student username.");
            return;
        }

        // Case-insensitive duplicate check
        const isDuplicate = studentList.some(
            existing => existing.toLowerCase() === username.toLowerCase()
        );

        if (isDuplicate) {
            setStudentError(`"${username}" has already been added to the list.`);
            return;
        }

        // Get fresh JWT token using the same method as EditBatch
        let currentJwt = jwt;
        try {
            const user = await Auth.currentAuthenticatedUser();
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken();
            currentJwt = accessToken.getJwtToken();
            setJwt(currentJwt);
        } catch (error) {
            console.error('Error getting JWT token:', error);
            setStudentError("Authentication error. Please sign out and sign in again.");
            return;
        }

        if (!currentJwt) {
            setStudentError("Authentication not ready. Please wait a moment and try again.");
            return;
        }

        try {
            try {
                const requestOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': currentJwt // No 'Bearer' prefix as per working implementation
                    },
                    body: JSON.stringify({
                        username: studentInput,
                        // Add timestamp to help with debugging
                        _debug: new Date().toISOString()
                    })
                };

                console.log('Sending request with options:', {
                    ...requestOptions,
                    headers: {
                        ...requestOptions.headers,
                        'Authorization': 'Bearer [REDACTED]' // Don't log the actual token
                    }
                });

                const response = await fetch(`https://api.prod.quizwizard.net/quiz/getuser`, requestOptions);

                if (response.status === 401) {
                    throw new Error('Session expired. Please refresh the page and try again.');
                }

                const data = await response.json();
                console.log('API response:', data);

                if (!response.ok) {
                    throw new Error(data.message || 'Failed to validate user');
                }

                if (data !== 'No User') {
                    // Check if user already has an active subscription
                    const subscriptionCheckResponse = await fetch("https://api.prod.quizwizard.net/quiz/getSubscription", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": currentJwt,
                        },
                        body: JSON.stringify({ username: studentInput }),
                    });

                    const subscriptionData = await subscriptionCheckResponse.json();
                    console.log('Subscription check response:', subscriptionData);

                    // Check if subscription is active
                    if (subscriptionData.status === 'active' || subscriptionData.premiumuser === '1') {
                        setStudentError(`User "${username}" already has an active subscription.`);
                    } else {
                        setStudentList(prev => [...prev, studentInput]);
                        setStudentInput('');
                        setStudentError('');
                    }
                } else {
                    setStudentError('Username not found. Please check the spelling and try again.');
                }
            } catch (error) {
                console.error('API Error:', error);
                setStudentError(error.message || 'An error occurred while validating the student.');
            }
        } catch (error) {
            console.error("Error validating student:", error);
            setStudentError("An error occurred while validating the student.");
        }
    };

    const handleRemoveStudent = (index) => {
        setStudentList(currentStudents => currentStudents.filter((_, i) => i !== index));
    };

    const handleStripePayment = async () => {
        if (!selectedPlan) {
            alert("Please select a plan!");
            return;
        }

        // For group plans, validate required fields
        if (activeTab === 'group') {
            // if (!teamDetails.schoolName?.trim()) {
            //     alert("Please enter your school/organization name");
            //     return;
            // }
            if (studentList.length === 0) {
                alert("Please add at least one student for the group plan.");
                return;
            }
            if (studentList.length > 100) {
                alert("Group size cannot exceed 100 users. Please contact support for larger groups.");
                return;
            }

            // Handle group payment flow directly here - don't call separate function
            try {
                setLoading(true);
                const user = await Auth.currentAuthenticatedUser();
                const username = user.username.includes("Google_") ? user.attributes.email : user.username;
                const email = user.attributes.email;

                // Use the correct group payment endpoint
                const url = "https://i3ppb217bi.execute-api.us-east-1.amazonaws.com/dev/GroupOrderPayment";

                const body = {
                    plan_type: selectedPlan,
                    students: studentList,
                    payer_username: username,
                    email: email
                };

                console.log("Body for group payment: ", body);

                const response = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: jwt },
                    body: JSON.stringify(body),
                });

                console.log("Response from payment API: ", response);

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`API request failed: ${errorText}`);
                }

                const data = await response.json();
                console.log("Data from payment API: ", data);

                if (!data.sessionId) {
                    throw new Error("Failed to create Stripe session.");
                }

                // Use group Stripe instance
                const stripe = await stripePromiseGroup;

                if (!stripe) {
                    throw new Error('Stripe not initialized for group payments. Check environment variables.');
                }

                console.log("Stripe instance:", stripe);
                await stripe.redirectToCheckout({ sessionId: data.sessionId });
                return;

            } catch (error) {
                console.error("Payment error:", error);
                alert(`Payment failed: ${error.message}`);
                setLoading(false);
                return;
            }
        }

        // Individual payment handling
        try {
            setLoading(true);
            const user = await Auth.currentAuthenticatedUser();
            const username = user.username.includes("Google_") ? user.attributes.email : user.username;
            const email = user.attributes.email;

            // Individual payment endpoint
            const url = "https://api.prod.quizwizard.net/quiz/stripePriceId";
            const body = { plan_type: selectedPlan, username, email, subscriptionType: 'individual' };

            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: jwt },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API request failed: ${errorText}`);
            }

            const data = await response.json();
            if (!data.sessionId) { throw new Error("Failed to create Stripe session."); }

            // Use individual Stripe instance
            const stripe = await stripePromiseIndividual;

            if (!stripe) {
                throw new Error(`Stripe not initialized for individual payments. Check environment variables.`);
            }

            console.log("Stripe instance:", stripe);
            await stripe.redirectToCheckout({ sessionId: data.sessionId });

        } catch (error) {
            console.error("Payment error:", error);
            alert(`Payment failed: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const addStudentButtonStyle = {
        marginLeft: '8px',
        color: 'orange',
        backgroundColor: '#fff',
        border: '1px solid orange',
        borderRadius: '5px',
        padding: '5px 10px',
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '1.2em'
    };

    return (
        <div className="plan-wrapper">
            <h1 className="plan-heading">Choose Your Plan</h1>
            <p className="plan-subtitle">Upgrade to unlock unlimited access</p>

            <div className="subscription-tabs">
                <button className={`tab-btn ${activeTab === 'individual' ? 'active' : ''}`} onClick={() => setActiveTab('individual')}>
                    Individual Plans
                </button>
                <button className={`tab-btn ${activeTab === 'group' ? 'active' : ''}`} onClick={() => setActiveTab('group')}>
                    Group Plans
                </button>
            </div>

            {activeTab === 'group' && (
                <div className="team-details-form">
                    {/* <div className="form-group">
                    <label htmlFor="schoolName">School/Organization Name *</label>
                    <input
                        type="text" 
                        id="schoolName" 
                        className="add-item-input"
                        name="schoolName" 
                        value={teamDetails.schoolName || ''}
                        onChange={(e) => setTeamDetails({ ...teamDetails, schoolName: e.target.value })}
                        placeholder="Enter your school or organization name" 
                        required
                    />
                </div> */}

                    <div className="form-group">
                        <label>Add users *</label>
                        <div className="add-student-instructions">
                            <p>Enter usernames one by one. These users will be added to your group subscription.</p>
                        </div>
                        <div className="add-item-box">
                            <input
                                value={studentInput}
                                onChange={(e) => setStudentInput(e.target.value)}
                                className="add-item-input"
                                placeholder="Enter username and press + to add"
                                onKeyPress={(e) => e.key === 'Enter' && handleAddStudent()}
                            />
                            <button
                                onClick={handleAddStudent}
                                className="add-student-btn"
                                title="Add student"
                            >
                                +
                            </button>
                        </div>
                        {studentError && <p className="error-message">{studentError}</p>}

                        {studentList.length > 0 && (
                            <div className="student-list-container">
                                <div className="student-list-header">
                                    <span>Users ({studentList.length})</span>
                                    <button
                                        className="clear-all-btn"
                                        onClick={() => setStudentList([])}
                                        disabled={studentList.length === 0}
                                    >
                                        Clear All
                                    </button>
                                </div>
                                <div className="student-list">
                                    {studentList.map((student, index) => (
                                        <div key={index} className="student-item">
                                            <span className="student-username">{student}</span>
                                            <button
                                                className="remove-student-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveStudent(index);
                                                }}
                                                title="Remove student"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {studentList.length > 0 && (
                        <div className="billing-summary">
                            <h3>Billing Summary</h3>
                            <div className="billing-details">
                                <div className="billing-row">
                                    <span>Plan:</span>
                                    <span>{planDetails.group[selectedPlan]?.name || 'N/A'}</span>
                                </div>
                                <div className="billing-row">
                                    <span>Number of users:</span>
                                    <span>{studentList.length}</span>
                                </div>
                                <div className="billing-row total">
                                    <span>Total:</span>
                                    <span>{selectedPlan ? `$${(planDetails.group[selectedPlan]?.baseAmount * studentList.length).toFixed(2)}` : 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="plan-flex">
                {Object.entries(planDetails[activeTab]).map(([planType, plan]) => {
                    const studentCount = activeTab === 'group' ? studentList.length : 1;
                    const totalAmount = (plan.baseAmount * (studentCount > 0 ? studentCount : 1)).toFixed(2);
                    const isDisabled = isSubscribed && activeTab === 'individual';

                    // Check if this is the current plan
                    let isCurrentPlan = false;

                    if (currentPlan && currentPlan.type === activeTab) {
                        // Direct match
                        if (currentPlan.plan === planType) {
                            isCurrentPlan = true;
                        }
                        // Check if this plan has the same duration as the current plan
                        else if (plan.duration && currentPlan.details?.duration === plan.duration) {
                            isCurrentPlan = true;
                            console.log('Matched plan by duration:', {
                                planType,
                                planDuration: plan.duration,
                                currentPlanDuration: currentPlan.details?.duration
                            });
                        }
                    }

                    console.log('Rendering plan card:', {
                        planType,
                        planName: plan.name,
                        isCurrentPlan,
                        currentPlan,
                        activeTab,
                        isDisabled,
                        planDetails: plan
                    });

                    // If this is the current plan, log more details
                    if (isCurrentPlan) {
                        console.log('Found current plan match:', {
                            planType,
                            planName: plan.name,
                            planDuration: plan.duration,
                            planCategory: activeTab
                        });
                    }

                    return (
                        <div
                            key={planType}
                            className={`plan-card 
                                ${selectedPlan === planType && !isDisabled ? 'selected' : ''} 
                                ${isDisabled ? 'disabled' : ''}
                                ${isCurrentPlan ? 'current-plan' : ''}
                            `}
                            onClick={isDisabled ? undefined : () => setSelectedPlan(planType)}
                        >
                            {/* {isCurrentPlan && (
                                <div className="current-plan-ribbon">Current Plan</div>
                            )} */}
                            <h2>{plan.name}</h2>
                            <p className="price">
                                ${totalAmount}
                                {activeTab === 'group' && studentList.length > 0 && (
                                    <span className="billing-period"> for {studentList.length} User(s)</span>
                                )}
                            </p>
                        </div>
                    );
                })}
            </div>

            <div className="plan-actions">
                {isLoadingSubscription ? (
                    <div className="subscription-loading">
                        <p>Loading subscription status...</p>
                    </div>
                ) : isSubscribed && activeTab === 'individual' ? (
                    <div className="already-subscribed">
                        <p>You already have an active subscription.</p>
                    </div>
                ) : (
                    <button
                        className="pay-btn"
                        disabled={!selectedPlan || loading}
                        onClick={handleStripePayment}
                    >
                        {loading ? "Processing..." : "Proceed to Payment"}
                    </button>
                )}
            </div>
        </div>
    );
};

export default SubscriptionPage;
